// Imports ------------------------------------------------------------------->
import {Component, attr, handler} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";
import * as array from "../../utils/array";
import {compareCoupon, isHeroCoupon} from "../../utils/coupons";

import {I18nLink} from "../flipp-i18n-link/I18nLink";
import {CouponClipping} from "../flipp-coupon-clipping/CouponClipping";
import {LoginButton} from "../flipp-login-button/LoginButton";
import {LoyaltyCardElement, AddLoyaltyCardElement} from "../flipp-loyalty-card/LoyaltyCardElement";
import {LoadingSpinner} from "../flipp-loading/LoadingSpinner";

import {inject} from "../../services/di";
import {ConfigService} from "../../services/ConfigService";
import {LocationService, Country} from "../../services/LocationService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {AuthenticationService} from "../../services/AuthenticationService";
import {AuthStatus} from "../../services/definitions/IAuthenticationService";
import {CouponService} from "../../services/CouponService";
import {LoyaltyProgramService} from "../../services/LoyaltyProgramService";
import {ClippingService} from "../../services/ClippingService";
import {Coupon, ActiveClippedCoupons} from "../../definitions/CouponDefinitions";
import {AuthProvider, LoyaltyCard} from "../../definitions/CommonDefinitions";
import {isCrawler} from "../../utils/crawler";

// Private Definitions ------------------------------------------------------->

const MAX_COUPONS = 50;

// Public Definitions -------------------------------------------------------->

// flipp-coupons-page ------------------------------------------------------->

interface CouponsPageProperties {
	category?: string;
	sort?: string;
}

@element("flipp-coupons-page")
export class CouponsPage extends Component<CouponsPageProperties, {}> {
	@attr category: string;
	@inject location: LocationService;
	@inject auth: AuthenticationService;
	@inject couponService: CouponService;
	@inject analytics: AnalyticsService;

	refs: {
		couponListing: CouponPageListing;
	};

	state = {
		sort: "all"
	};

	renderLoading() {
		return <div>
			<div class="content">
				<header class="intro"></header>
			</div>
		</div>;
	}

	render() {
		return <div>
			<div class="content">
				<header class="intro">
					<CouponPageHeader />
				</header>

				{isCrawler() ? this.getH1() : null}
				<CouponsPageTabs sort={this.state.sort} couponTypeChange={this._handleCouponSort}/>
				<CouponPageListing ref="couponListing" sort={this.state.sort} category={this.category} />
			</div>
		</div>;
	}

	private _handleCouponSort = (sort: string) => {
		this.state.sort = sort;
		this.refs.couponListing.sort = sort;

		const redemptionMethod = this.state.sort !== "all" ? this.state.sort : null;
		this.couponService.getCoupons(redemptionMethod).then((coupons) => {
			const filteredCoupons = coupons.filter(coupon =>
				array.contains(coupon.categories, this.category));

			this.analytics.trackCouponListing(this.category, filteredCoupons, sort);
		});
	}

	private getH1() {
		const { category } = this.props;
		const city = this.location.city;
		const i18nKey = category === "All Deals" ? "index_city_coupon_h1" : "index_coupon_category_h1";
		const h1 = i18n(i18nKey, {city, category: i18n(this.props.category)});
		if (typeof category !== "string") {
			return null;
		}
		return <h1>{h1}</h1>;
	}
}

// flipp-coupon-page-tabs ----------------------------------------------->


interface CouponsPageTabsProperties {
	sort: string;
	couponTypeChange: (sort: string) => void;
}

@element("flipp-coupons-page-tabs")
class CouponsPageTabs extends Component<CouponsPageTabsProperties, {}> {
	@attr sort: string;
	@handler couponTypeChange: (sort: string) => void;
	@inject location: LocationService;

	render() {
		if (this.location.country === Country.CANADA) {
			return null;
		}

		return <section class="sort-tabs" flex-horizontal>
				<button title={i18n("card_category_all")}
								aria-label={i18n("card_category_all")}
								classList={{selected: this.sort === "all"}}
								onClick={this.handleCouponSort_.bind(this, "all")}>
					<div flex-horizontal>
						<img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/coupon-outline-default5.svg" alt=""/>
						<span class="tab">{i18n("card_category_all")}</span>
					</div>
				</button>
				<button title={i18n("card_category_card")}
								aria-label={i18n("card_category_card")}
								classList={{selected: this.sort === "savetocard"}}
								onClick={this.handleCouponSort_.bind(this, "savetocard")}>
					<div flex-horizontal>
						<img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/card-default5.svg" alt=""/>
						<span>{i18n("card_category_card")}</span>
					</div>
				</button>
				<button title={i18n("card_category_print")}
								aria-label={i18n("card_category_print")}
								classList={{selected: this.sort === "print"}}
								onClick={this.handleCouponSort_.bind(this, "print")}>
					<div flex-horizontal>
						<img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/print-default5.svg" alt=""/>
						<span>{i18n("card_category_print")}</span>
					</div>
				</button>
			</section>;
	}

	private handleCouponSort_ = (sort: string, e: Event) => {
		e.preventDefault();
		this.sort = sort;
		this.couponTypeChange(sort);
	}
}


// flipp-coupon-page-listing ------------------------------------------------>

interface CouponPageListingProperties extends JSX.HTMLProps {
	category?: string;
	sort?: string;
}

@element("flipp-coupon-page-listing")
export class CouponPageListing extends Component<CouponPageListingProperties, Coupon[]> {

	@inject auth: AuthenticationService;
	@inject loyaltyService: LoyaltyProgramService;
	@inject couponService: CouponService;
	@inject clippingService: ClippingService;
	@inject location: LocationService;
	@attr sort: string;
	@attr category: string;

	state = {
		couponsVisible: MAX_COUPONS
	};

	onAttach() {
		window.addEventListener("flipp-auth", this.refreshClippings_);
		window.addEventListener("flipp-loyaltycards", this.refreshClippings_);
		window.addEventListener("flipp-couponclipped", this.refreshClippings_);
	}

	onDetach() {
		window.removeEventListener("flipp-auth", this.refreshClippings_);
		window.removeEventListener("flipp-loyaltycards", this.refreshClippings_);
		window.addEventListener("flipp-couponclipped", this.refreshClippings_);
	}

	prepare() {
		/* Call getActiveClippedCoupons here even though we don't actually require the results here. This is
		 * because the children CouponClipping components require those results, and we shouldn't render any
		 * of the CouponsPage component until it returns */
		let getClippedCouponsFn = Promise.resolve<ActiveClippedCoupons>([]);
		if (this.auth.status === AuthStatus.LOGGED_IN) {
			getClippedCouponsFn = this.couponService.getActiveClippedCoupons(this.auth.user);
		}

		const redemptionMethod = this.sort !== "all" ? this.sort : null;
		return Promise.all([
			this.couponService.getCoupons(redemptionMethod),
			getClippedCouponsFn
		]).then(([coupons, ]) => {
			if (!this.category) {
				return coupons;
			}

			return coupons.filter((coupon) =>
				array.contains(coupon.categories, this.category));
		});
	}

	renderLoading() {
		return <LoadingSpinner />;
	}

	render(coupons: Coupon[]) {
		const heroCoupons: Coupon[] = [];
		let rest: Coupon[] = [];
		coupons.forEach((coupon) => {
			if (isHeroCoupon(coupon)) {
				heroCoupons.push(coupon);
			} else {
				rest.push(coupon);
			}
		});
		heroCoupons.sort(compareCoupon);
		rest.sort(compareCoupon);

		if (heroCoupons.length === 0 && rest.length === 0) {
			return <div class="no-coupons">
					<p>{i18n("no_coupons_for_postal_code",
						{postal_code: this.location.postalCode})}</p>
				</div>;
		}

		if (heroCoupons.length > this.state.couponsVisible) {
			// Case 1: number of hero coupons exceeds what we can show. Only show the hero coupons that we can
			heroCoupons.splice(0, this.state.couponsVisible);
			rest = [];
		} else {
			// Case 2: display a combination of hero and regular coupons
			const startIdx = this.state.couponsVisible - heroCoupons.length;
			const numToDelete = rest.length - startIdx;
			rest.splice(startIdx, numToDelete);
		}

		return <div>
				<section>
					<div class="coupons">{heroCoupons.map((coupon) => {
							return <CouponClipping couponId={coupon.id} />;
						})}
					</div>
					<div class="coupons">{rest.map((coupon) => {
							return <CouponClipping couponId={coupon.id} />;
						})}
					</div>
				</section>
				{coupons.length > MAX_COUPONS ?
					<div class="button-area" flex-horizontal>
						<button classList={{
							disabled: this.state.couponsVisible >= coupons.length
						}} onClick={this.handleShowMore_}>
							<span>{i18n("show_more")}</span>
						</button>
					</div>
				:
					null
				}
			</div>;
	}

	private handleShowMore_ = (e: Event) => {
		e.preventDefault();
		this.state.couponsVisible += MAX_COUPONS;
		this.invalidate();
	}

	private refreshClippings_ = (e: Event) => {
		this.invalidate();
	}

}

// flipp-coupon-page-header ------------------------------------------------->

@element("flipp-coupon-page-header")
export class CouponPageHeader extends Component<{}, {}> {

	@inject auth: AuthenticationService;
	@inject location: LocationService;
	@inject config: ConfigService;

	onAttach() {
		window.addEventListener("flipp-auth", this.handleAuthChange_);
	}

	onDetach() {
		window.removeEventListener("flipp-auth", this.handleAuthChange_);
	}

	render() {
		const isCanada = this.location.country === Country.CANADA;
		return <div>
			{this.auth.status === AuthStatus.LOGGED_IN ?
				<section class="loyalty-cards" flex-vertical>
					<h2>{i18n("my_cards")}</h2>
					<div flex-horizontal>
						<LoyaltyCardListElement />
						<AddLoyaltyCardElement />
					</div>
				</section>
			:
				<div flex-horizontal>
					<img class="clipping" src="https://f.wishabi.net/creative/Distribution/images/printcoupons.png" alt="" />
					<div class="login-message" flex-horizontal>
						<div flex-vertical>
							<h2>{isCanada ? i18n("coupon_heading_can") : i18n("coupon_heading_usa")}</h2>
							<p>{isCanada ?
								<I18nLink key="coupon_sub_heading_can" values={JSON.stringify({1: "https://app.flipp.com/"})} /> :
								i18n("coupon_sub_heading_usa")}
							</p>
						</div>
						<div class="button-area" flex-horizontal>
							<LoginButton provider={AuthProvider.GOOGLE} context="coupon_page" />
							<LoginButton provider={AuthProvider.FACEBOOK} context="coupon_page" />
						</div>
					</div>
				</div>
			}
		</div>;
	}

	private handleAuthChange_ = (e: Event) => {
		this.invalidate();
	}
}

// flipp-loyalty-card-list -------------------------------------------------->

type LoyaltyCardListModel = [number[], LoyaltyCard[]];

@element("flipp-loyalty-card-list")
export class LoyaltyCardListElement extends Component<{}, LoyaltyCardListModel> {

	@inject auth: AuthenticationService;
	@inject loyaltyService: LoyaltyProgramService;

	prepare() {
		let loyaltyCardsFn = Promise.resolve<LoyaltyCard[]>([]);
		if (this.auth.status === AuthStatus.LOGGED_IN) {
			loyaltyCardsFn = this.loyaltyService.getLoyaltyCards(this.auth.user);
		}
		return Promise.all([
			this.loyaltyService.getLoyaltyPrograms(),
			loyaltyCardsFn
		]).then(([programs, loyaltyCards]) => {
			const availableProgramIds = programs.map((program) => program.id);
			const sortedCards = loyaltyCards.sort((a, b) => {
				if (a.name < b.name) return -1;
				if (a.name > b.name) return 1;
				return 0;
			}).filter((card) => availableProgramIds.indexOf(card.loyaltyProgramId) !== -1);

			return [availableProgramIds, sortedCards] as LoyaltyCardListModel;
		});
	}

	onAttach() {
		window.addEventListener("flipp-loyaltycards", this.handleLoyaltyCardEvent_);
	}

	onDetach() {
		window.removeEventListener("flipp-loyaltycards", this.handleLoyaltyCardEvent_);
	}

	renderLoading() {
		return this._doRender(true, [[], []]);
	}

	render(model: LoyaltyCardListModel) {
		return this._doRender(false, this.model);
	}

	private _doRender(loading: boolean, [programs, loyaltyCards]: LoyaltyCardListModel) {
		return <div flex-horizontal>
				{!loading && loyaltyCards ?
						loyaltyCards.map((card) => {
							if (programs.indexOf(card.loyaltyProgramId) !== -1) {
								return <LoyaltyCardElement
									cardId={card.id}
									loyaltyProgramId={card.loyaltyProgramId} />;
							}
						})
				:
					null
				}
			</div>;
	}

	private handleLoyaltyCardEvent_ = (e: CustomEvent) => {
		this.invalidate();
	}
}
